import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { STORAGE_KEYS } from "@/constants/storage";
import SafeLocalStorage from "@/utils/safeLocalStorage";
import { loginRequest } from "@/authConfig";
import { callApi } from "@/utils/callApi";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [status, setStatus] = useState("Processing authentication...");

  useEffect(() => {
    // Declare variables at useEffect scope level for proper access
    let userData = null;
    let hasApiAccess = false;
      
    const handleRedirectResponse = async () => {
      try {
        console.log("AuthCallback: Processing Azure redirect response...");
        setStatus("Verifying credentials...");
        
        // Handle the response from Azure - this should already be handled by main.jsx
        const response = await instance.handleRedirectPromise();
        
        if (response && response.account) {
          console.log("AuthCallback: Authentication successful", {
            account: response.account.username,
            accessToken: response.accessToken ? 'Present' : 'Missing'
          });
          
          setStatus("Setting up admin access...");
          
          // Get access token for API calls
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: response.account,
          });

          console.log("AuthCallback: Admin access token acquired:", tokenResponse);

          // Test backend API authentication with a lightweight admin endpoint
          try {
            console.log("AuthCallback: Testing backend API authentication...");
            setStatus("Verifying API access...");
            
            // Test authentication with surveys endpoint (lightweight query)
            const apiTestResponse = await callApi(
              `${import.meta.env.VITE_API_URL}/api/surveys?maxResults=1`,
              { method: 'GET' },
              instance,
              [response.account]
            );
            
            console.log("AuthCallback: Backend API accessible:", apiTestResponse);
            
            // Set flag based on successful API call
            hasApiAccess = apiTestResponse !== null && apiTestResponse !== undefined;
            
            // Create userData with verified API access
            userData = {
              id: response.account.homeAccountId,
              name: response.account.name || "Admin User",
              email: response.account.username,
              role: "admin",
              accessToken: tokenResponse.accessToken,
              permissions: ["admin_access", "manage_content"]
            };
            
            console.log("AuthCallback: API access verified, userData created:", userData);
            
          } catch (apiError) {
            console.warn("AuthCallback: API access test failed:", apiError);
            
            // Create fallback userData even if API fails
            userData = {
              id: response.account.homeAccountId,
              name: response.account.name || "Admin User",
              email: response.account.username,
              role: "admin",
              accessToken: tokenResponse.accessToken,
              permissions: ["admin_access"]
            };
            
            hasApiAccess = false;
            console.log("AuthCallback: Using fallback admin user data (API unavailable):", userData);
          }

          // Store auth session regardless of API success/failure
          SafeLocalStorage.set(STORAGE_KEYS.AUTH_SESSION, {
            isAuthenticated: true,
            lastLogin: new Date().toISOString(),
            accountId: response.account.homeAccountId,
            hasApiAccess: hasApiAccess
          });
          
          setStatus("Redirecting to admin portal...");
          
          // Navigate to admin dashboard
          console.log("AuthCallback: Navigating to admin with hasApiAccess:", hasApiAccess);
          navigate("/admin");
          
        } else if (accounts.length > 0) {
          console.log("AuthCallback: Using existing account", accounts[0].username);
          // User is already authenticated, redirect to admin
          navigate("/admin");
        } else {
          console.log("AuthCallback: No authentication found, redirecting to login");
          setStatus("Authentication failed. Redirecting...");
          navigate("/");
        }
      } catch (error) {
        console.error("AuthCallback: Error handling redirect:", error);
        setStatus(`Authentication error: ${error.message}`);
        setTimeout(() => {
          navigate("/?error=auth_failed");
        }, 3000);
      }
    };

    handleRedirectResponse();
  }, [instance, navigate, accounts]);

  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center", 
      minHeight: "50vh",
      flexDirection: "column" 
    }}>
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <h4>{status}</h4>
      <p className="text-muted">Please wait while we complete your sign-in with Microsoft.</p>
    </div>
  );
}